import React, { useEffect, useRef } from "react";
import Quill from "quill";
import DOMPurify from "dompurify";
import "quill/dist/quill.snow.css";

const toolbar = [
  [{ header: [1, 2, 3, 4, 5, 6, false] }, { font: [] }],
  [{ size: ["small", false, "large", "huge"] }],
  ["bold", "italic", "underline", "strike"],
  [{ color: [] }, { background: [] }],
  [{ script: "sub" }, { script: "super" }],
  [{ list: "ordered" }, { list: "bullet" }],
  [{ indent: "-1" }, { indent: "+1" }, { align: [] }, { direction: "rtl" }],
  ["blockquote", "code-block", "link", "image"],
  ["clean"],
];

const pickerLabels = {
  header: "Heading", font: "Font", size: "Font size",
  color: "Text color", background: "Highlight color", align: "Alignment",
};

export default function BroadcastEditor({ editorRef, onChange, invalid }) {
  const hostRef = useRef(null);

  useEffect(() => {
    const host = hostRef.current;
    const container = document.createElement("div");
    host.appendChild(container);
    const editor = new Quill(container, {
      theme: "snow",
      placeholder: "Write the message users should see",
      modules: { toolbar, history: { userOnly: true } },
    });
    editorRef.current = editor;
    editor.root.id = "broadcast-message";
    editor.root.setAttribute("role", "textbox");
    editor.root.setAttribute("aria-multiline", "true");
    editor.root.setAttribute("aria-labelledby", "broadcast-message-label");
    const update = () => onChange(DOMPurify.sanitize(editor.root.innerHTML));
    editor.on("text-change", update);

    host.querySelectorAll(".ql-toolbar button").forEach((button) => {
      const format = Array.from(button.classList).find((name) => name.startsWith("ql-"));
      const label = `${format?.slice(3).replace(/-/g, " ") || "Format"} ${button.value || ""}`.trim();
      button.setAttribute("aria-label", label);
      button.setAttribute("title", label);
    });
    host.querySelectorAll(".ql-picker").forEach((picker) => {
      const format = Array.from(picker.classList).find((name) => pickerLabels[name.slice(3)]);
      const label = pickerLabels[format?.slice(3)];
      const control = picker.querySelector(".ql-picker-label");
      if (label && control) {
        control.setAttribute("aria-label", label);
        control.setAttribute("title", label);
      }
    });

    return () => {
      editor.off("text-change", update);
      editorRef.current = null;
      host.replaceChildren();
    };
  }, [editorRef, onChange]);

  useEffect(() => {
    const root = editorRef.current?.root;
    root?.setAttribute("aria-invalid", String(invalid));
    if (invalid) root?.setAttribute("aria-describedby", "broadcast-message-error");
    else root?.removeAttribute("aria-describedby");
  }, [editorRef, invalid]);

  return (
    <div className={`broadcast-editor ${invalid ? "broadcast-editor-invalid" : ""}`}>
      <div className="broadcast-editor-history">
        <button type="button" className="btn btn-sm btn-light mr-1" onClick={() => editorRef.current?.history.undo()}>Undo</button>
        <button type="button" className="btn btn-sm btn-light" onClick={() => editorRef.current?.history.redo()}>Redo</button>
      </div>
      <div ref={hostRef} />
    </div>
  );
}
